import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { showLoadingSpinnerAtom } from "../../../lander-quiz/lander-quiz.state";
import { FKCService } from "../../fkc.service";
import { pagesAtom, termsAndPrivacyAtom, userDataAtom } from "../../fkc.state";
import { FallbackPages, Pages } from "../../fkc.types";
import { ActionButton } from "../shared/submit-button/submit-button.component";

type FormData = z.infer<typeof formSchema>;

const formSchema = z.object({
  email: z.string().email(),
  password: z
    .string()
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/, {
      message:
        "Password must contain at least 1 number, 1 special character, 1 uppercase letter, 1 lowercase letter, and must be at least 8 characters long",
    }),
  termsAndPrivacy: z.literal<boolean>(true, {
    errorMap: () => ({
      message: "You must agree to the Terms & Conditions and Privacy Policy",
    }),
  }),
});

export function RegistrationPage(): JSX.Element {
  const [, setPage] = useAtom(pagesAtom);
  const [termsAndPrivacy] = useAtom(termsAndPrivacyAtom);
  const [, setIsLoading] = useAtom(showLoadingSpinnerAtom);
  const [, setUserData] = useAtom(userDataAtom);

  const registerUser = useCallback(
    (data: FormData) => {
      setIsLoading(true);

      data.termsAndPrivacy &&
        FKCService.signUp(data.email, data.password)
          .then((publisherUserId) => {
            setUserData({
              publisher_user_id: publisherUserId,
              cards_count: 0,
              is_verified: false,
            });

            return FKCService.acceptTermsAndConditions(
              publisherUserId,
              termsAndPrivacy.id
            ).then(() => setPage(Pages.VERIFICATION));
          })
          .catch(() => setPage(FallbackPages.REGISTRATION_RETRY))
          .finally(() => setIsLoading(false));
    },
    [termsAndPrivacy.id, setPage, setIsLoading, setUserData]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: window.theme.Sweepstake.prototype.retrieveSubmission()?.data
        ?.email,
    },
  });

  return (
    <>
      <strong className={clsx("fkc__children--title", "center")}>
        Enter your email and create a password
      </strong>
      <form
        className="fkc__registration--form"
        onSubmit={handleSubmit(registerUser)}
      >
        <input
          type="email"
          placeholder="Email"
          aria-label="Enter your email"
          className="fkc__registration--input"
          disabled
          {...register("email")}
        />
        <input
          type="password"
          placeholder="Password"
          aria-label="Enter your password"
          className="fkc__registration--input"
          {...register("password")}
        />
        <div className="fkc__registration--terms-container">
          <div className="fkc__registration--terms-checkbox-container">
            <input
              id="terms-and-privacy"
              type="checkbox"
              defaultChecked
              {...register("termsAndPrivacy")}
            />
            <label
              htmlFor="terms-and-privacy"
              className="fkc__registration--terms-checkbox-label"
            >
              I agree to{" "}
              <a href={termsAndPrivacy.url} target="_blank" rel="noreferrer">
                FindKeepCash Terms & Conditions and Privacy Policy
              </a>
            </label>
          </div>
          <ul className="fkc__registration--error">
            {Object.values(errors).map((error) => (
              <li key={error.message}>{error.message}</li>
            ))}
          </ul>

          <ActionButton
            disabled={Object.keys(errors).length > 0}
            type="submit"
            className="full-width"
          >
            Next
          </ActionButton>
        </div>
      </form>
    </>
  );
}
